import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import cccApi, { type CccHistoryEvent } from '../api/cccApi';
import { prospeccaoSubstageLabel } from '../config/prospeccaoWorkspace';
import { normalizeWorkspaceSubstage, subsectionLabel } from '../config/recuperacaoWorkspace';
import { normalizeRetencaoSubstage, retencaoSubstageLabel } from '../config/retencaoWorkspace';

export type CccClientHistoryWorkspace = 'prospeccao' | 'recuperacao' | 'retencao';

const formatDateTime = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

type HistoryCategory =
  | 'all'
  | 'solicitar_proposta'
  | 'iniciar_caso'
  | 'status_update'
  | 'alteracao_comercial';

const historyEventCategory = (event: CccHistoryEvent): Exclude<HistoryCategory, 'all'> => {
  if (event.event_type === 'alteracao_comercial') return 'alteracao_comercial';
  if (event.event_type === 'solicitar_proposta') return 'solicitar_proposta';
  if (event.event_type === 'status_update') return 'status_update';
  return 'iniciar_caso';
};

const historyCategoryLabel = (category: Exclude<HistoryCategory, 'all'>) => {
  if (category === 'alteracao_comercial') return 'Alteração do Comercial';
  if (category === 'solicitar_proposta') return 'Solicitar Proposta';
  if (category === 'status_update') return 'Atualização do Estado';
  return 'Início';
};

const historyEventTypeLabel = (eventType: CccHistoryEvent['event_type']) => {
  if (eventType === 'alteracao_comercial') return 'Alteração do Comercial';
  if (eventType === 'solicitar_proposta') return 'Solicitar Proposta';
  if (eventType === 'status_update') return 'Atualização do Estado';
  return 'Início';
};

type Props = {
  workspace: CccClientHistoryWorkspace;
  entityId: string;
  refreshKey?: number;
  /** Modal uses a vertical list; panel keeps the horizontal timeline. */
  variant?: 'panel' | 'modal';
};

const CccClientHistoryPanel: React.FC<Props> = ({
  workspace,
  entityId,
  refreshKey = 0,
  variant = 'panel',
}) => {
  const isModal = variant === 'modal';
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<CccHistoryEvent[]>([]);
  const [historyTypeFilter, setHistoryTypeFilter] = useState<HistoryCategory>('all');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<string>('all');
  const [activeHistoryKey, setActiveHistoryKey] = useState<string | null>(null);

  const historyStatusLabel = useCallback(
    (value?: string | null) => {
      if (!value) return '—';
      const raw = String(value);
      if (workspace === 'prospeccao') return prospeccaoSubstageLabel(raw);
      if (workspace === 'retencao') {
        return retencaoSubstageLabel(normalizeRetencaoSubstage(raw));
      }
      return subsectionLabel(normalizeWorkspaceSubstage(raw));
    },
    [workspace]
  );

  const historyEventSummary = useCallback(
    (event: CccHistoryEvent) => {
      if (event.event_type === 'alteracao_comercial') {
        const fromName = event.details?.old_user_name || 'Sem comercial';
        const toName = event.details?.new_user_name || '—';
        return `Comercial: ${fromName} → ${toName}`;
      }
      if (event.event_type === 'status_update') {
        const fromStatus = event.details?.old_substage
          ? historyStatusLabel(event.details.old_substage)
          : null;
        const toStatus = event.details?.new_substage
          ? historyStatusLabel(event.details.new_substage)
          : null;
        return fromStatus
          ? `Estado: ${fromStatus} → ${toStatus || 'Sem estado'}`
          : `Novo estado: ${toStatus || 'Sem estado'}`;
      }
      if (event.event_type === 'solicitar_proposta') {
        const source = String(event.details?.source || '');
        if (source.endsWith('prepare_solicitar_proposta')) {
          const toStatus = event.details?.new_substage
            ? historyStatusLabel(event.details.new_substage)
            : null;
          return toStatus
            ? `Solicitar Proposta iniciado (${toStatus})`
            : 'Solicitar Proposta iniciado';
        }
        return event.details?.proposta_id
          ? `Pedido de proposta submetido (#${event.details.proposta_id})`
          : 'Pedido de proposta submetido';
      }
      if (event.event_type === 'lead_created') {
        const empresa = event.details?.nome_empresa;
        const to = historyStatusLabel(event.details?.new_substage);
        return empresa ? `Lead "${empresa}" criada em ${to}` : 'Nova lead registada';
      }
      if (event.event_type === 'workspace_claim') {
        return workspace === 'retencao'
          ? 'Marcação iniciada na área de trabalho de Reposição'
          : 'Marcação iniciada na área de trabalho de Recuperação';
      }
      return event.title || 'Registo de criação da marcação';
    },
    [historyStatusLabel, workspace]
  );

  const historyEventStatusLabels = useCallback(
    (event: CccHistoryEvent) => {
      const out: string[] = [];
      if (event.details?.old_substage) out.push(historyStatusLabel(event.details.old_substage));
      if (event.details?.new_substage) out.push(historyStatusLabel(event.details.new_substage));
      return Array.from(new Set(out));
    },
    [historyStatusLabel]
  );

  const emptyMessage =
    workspace === 'prospeccao'
      ? 'Sem eventos relevantes no histórico desta lead.'
      : 'Sem eventos relevantes no histórico desta marcação.';

  useEffect(() => {
    let cancelled = false;
    setHistoryLoading(true);

    const load =
      workspace === 'prospeccao'
        ? cccApi.getProspeccaoLeadHistory(entityId)
        : workspace === 'recuperacao'
          ? cccApi.getRecuperacaoMarcacaoHistory(entityId)
          : cccApi.getRetencaoMarcacaoHistory(entityId);

    load
      .then((res) => {
        if (!cancelled) {
          setHistoryEvents(Array.isArray(res.events) ? res.events : []);
          setHistoryTypeFilter('all');
          setHistoryStatusFilter('all');
          setActiveHistoryKey(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Erro ao carregar histórico';
        message.error(msg);
        setHistoryEvents([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspace, entityId, refreshKey]);

  const historyStatusOptions = useMemo(() => {
    const set = new Set<string>();
    historyEvents.forEach((event) => {
      historyEventStatusLabels(event).forEach((status) => set.add(status));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [historyEvents, historyEventStatusLabels]);

  const filteredHistoryEvents = useMemo(
    () =>
      [...historyEvents]
        .sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1))
        .filter((event) => {
          if (historyTypeFilter !== 'all' && historyEventCategory(event) !== historyTypeFilter) {
            return false;
          }
          if (historyStatusFilter === 'all') return true;
          const statuses = historyEventStatusLabels(event);
          return statuses.includes(historyStatusFilter);
        }),
    [historyEvents, historyTypeFilter, historyStatusFilter, historyEventStatusLabels]
  );

  const timelineRows = useMemo(() => {
    const chunkSize = isModal ? 999 : 14;
    const rows: CccHistoryEvent[][] = [];
    for (let i = 0; i < filteredHistoryEvents.length; i += chunkSize) {
      rows.push(filteredHistoryEvents.slice(i, i + chunkSize));
    }
    return rows;
  }, [filteredHistoryEvents, isModal]);

  const activeHistoryEvent = useMemo(() => {
    if (!activeHistoryKey) return filteredHistoryEvents[0] || null;
    const [ts, type, rowIdxRaw, idxRaw] = activeHistoryKey.split('|');
    const rowIdx = Number(rowIdxRaw);
    const idx = Number(idxRaw);
    const row = timelineRows[rowIdx];
    const item = row?.[idx];
    if (!item) return filteredHistoryEvents[0] || null;
    if (item.timestamp !== ts || item.event_type !== type) return filteredHistoryEvents[0] || null;
    return item;
  }, [activeHistoryKey, filteredHistoryEvents, timelineRows]);

  useEffect(() => {
    if (filteredHistoryEvents.length === 0) {
      setActiveHistoryKey(null);
      return;
    }
    if (!activeHistoryKey) {
      const first = filteredHistoryEvents[0];
      setActiveHistoryKey(`${first.timestamp}|${first.event_type}|0|0`);
    }
  }, [filteredHistoryEvents, activeHistoryKey]);

  const renderDetails = () =>
    activeHistoryEvent ? (
      <>
        <div className="rec-history-details-head">
          <span
            className={`rec-history-tag rec-history-tag--${historyEventCategory(activeHistoryEvent)}`}
          >
            {historyCategoryLabel(historyEventCategory(activeHistoryEvent))}
          </span>
          <h5>{activeHistoryEvent.title}</h5>
        </div>
        <p>
          <strong>Quando:</strong> {formatDateTime(activeHistoryEvent.timestamp)}
        </p>
        <p>
          <strong>Quem:</strong> {activeHistoryEvent.by_name || 'Utilizador'}
        </p>
        <p>
          <strong>Evento:</strong> {historyEventSummary(activeHistoryEvent)}
        </p>
      </>
    ) : (
      <p>Selecione um evento para ver detalhes.</p>
    );

  return (
    <section className={`rec-history ${isModal ? 'rec-history--modal' : ''}`}>
      <div className="rec-history-filters">
        <label className="rec-history-filter">
          <span>Tipo de evento</span>
          <select
            className="rec-select"
            value={historyTypeFilter}
            onChange={(e) => setHistoryTypeFilter(e.target.value as HistoryCategory)}
          >
            <option value="all">Todos</option>
            <option value="iniciar_caso">Início</option>
            <option value="status_update">Atualização do Estado</option>
            <option value="solicitar_proposta">Solicitar Proposta</option>
            <option value="alteracao_comercial">Alteração do Comercial</option>
          </select>
        </label>
        <label className="rec-history-filter">
          <span>Estado relacionado</span>
          <select
            className="rec-select"
            value={historyStatusFilter}
            onChange={(e) => setHistoryStatusFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            {historyStatusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
      </div>
      {historyLoading ? (
        <p className="rec-lead-panel-empty">A carregar histórico…</p>
      ) : filteredHistoryEvents.length === 0 ? (
        <p className="rec-lead-panel-empty">{emptyMessage}</p>
      ) : isModal ? (
        <div className="rec-history-layout rec-history-layout--modal">
          <aside className="rec-history-details">{renderDetails()}</aside>
          <ul className="rec-history-modal-list" aria-label="Linha do tempo de eventos">
            {filteredHistoryEvents.map((event, index) => {
              const pointKey = `${event.timestamp}|${event.event_type}|0|${index}`;
              const category = historyEventCategory(event);
              const statuses = historyEventStatusLabels(event);
              return (
                <li key={pointKey}>
                  <button
                    type="button"
                    className={`rec-history-modal-item rec-history-modal-item--${category} ${
                      activeHistoryKey === pointKey ? 'is-active' : ''
                    }`}
                    onClick={() => setActiveHistoryKey(pointKey)}
                  >
                    <span className={`rec-history-tag rec-history-tag--${category}`}>
                      {historyEventTypeLabel(event.event_type)}
                    </span>
                    <span className="rec-history-modal-item-main">
                      <span className="rec-history-modal-item-title">{event.title}</span>
                      <span className="rec-history-modal-item-summary">
                        {historyEventSummary(event)}
                      </span>
                    </span>
                    <span className="rec-history-modal-item-meta">
                      <span>{formatDateTime(event.timestamp)}</span>
                      {statuses.length > 0 ? (
                        <span className="rec-history-modal-item-status">
                          {statuses[statuses.length - 1]}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div className="rec-history-layout">
          <div className="rec-history-horizontal">
            {timelineRows.map((row, rowIdx) => (
              <div
                key={`history-row-${rowIdx}`}
                className={`rec-history-row ${row.length <= 1 ? 'is-single' : ''}`}
                style={{ ['--row-points' as string]: String(row.length) }}
              >
                <span className="rec-history-horizontal-line" aria-hidden />
                <ol className="rec-history-points">
                  {row.map((event, index) => {
                    const statuses = historyEventStatusLabels(event);
                    const pointKey = `${event.timestamp}|${event.event_type}|${rowIdx}|${index}`;
                    return (
                      <li
                        key={`${event.timestamp}-${event.event_type}-${rowIdx}-${index}`}
                        className={`rec-history-point-wrap rec-history-node--${historyEventCategory(event)} ${
                          index === row.length - 1 ? 'is-last' : ''
                        } ${activeHistoryKey === pointKey ? 'is-active' : ''}`}
                      >
                        <button
                          type="button"
                          className="rec-history-point"
                          aria-label={`${historyEventTypeLabel(event.event_type)} em ${formatDateTime(
                            event.timestamp
                          )}`}
                          onMouseEnter={() => setActiveHistoryKey(pointKey)}
                          onFocus={() => setActiveHistoryKey(pointKey)}
                        />
                        {index < row.length - 1 && (
                          <span className="rec-history-arrow" aria-hidden>
                            ➜
                          </span>
                        )}
                        <span className="rec-history-point-time">
                          {new Date(event.timestamp).toLocaleTimeString('pt-PT', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span className="rec-history-point-type">
                          {historyEventTypeLabel(event.event_type)}
                        </span>
                        <span className="rec-history-point-status">
                          {statuses[statuses.length - 1] || '—'}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </div>
          <aside className="rec-history-details">{renderDetails()}</aside>
        </div>
      )}
    </section>
  );
};

export default CccClientHistoryPanel;
